import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { HStack } from "@design-system/HStack/HStack";
import type { OrganizationTaskTemplateRow } from "../organizationTaskTemplatesViewConfig";
import { getOrganizationTaskTemplatesTableMeta } from "../organizationTaskTemplatesTableMeta";

type MemberField = "createdByUserId" | "updatedByUserId";

const createMemberCell = (field: MemberField, displayName: string) => {
    const Cell = memo(function OrganizationTaskTemplateMemberCell({
        row,
        table,
    }: CellContext<ViewTableFeatures, OrganizationTaskTemplateRow, string>) {
        const { data: members = [] } = useCurrentOrganizationMembers();
        const meta = getOrganizationTaskTemplatesTableMeta(table);
        const userId = meta?.templatesById.get(row.original.id)?.[field];
        const member = useMemo(
            () =>
                userId == null
                    ? null
                    : (members.find((m) => m.user.id === userId) ?? null),
            [userId, members],
        );

        if (
            row.original.kickDefaultOption !== undefined &&
            field === "createdByUserId"
        ) {
            return (
                <ViewTableCell>
                    <HStack gap={8} align="center">
                        <KickAvatar size="sm" aria-hidden />
                        Kick
                    </HStack>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                {member ? (
                    <HStack gap={8} align="center">
                        <UserAvatar user={member.user} size="sm" aria-hidden />
                        {member.user.name}
                    </HStack>
                ) : (
                    <span className="text-muted">-</span>
                )}
            </ViewTableCell>
        );
    });
    Cell.displayName = displayName;
    return Cell;
};

export const OrganizationTaskTemplateCreatedByCell = createMemberCell(
    "createdByUserId",
    "OrganizationTaskTemplateCreatedByCell",
);

export const OrganizationTaskTemplateUpdatedByCell = createMemberCell(
    "updatedByUserId",
    "OrganizationTaskTemplateUpdatedByCell",
);
